import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { ScrambleText } from "@/components/site/ScrambleText";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES, ACCENTS } from "@/data/site";
const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const reduce = useReducedMotion();
  const imgRef = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 15 });
  const sry = useSpring(ry, { stiffness: 150, damping: 15 });

  const { scrollY } = useScroll();
  const parallax = useTransform(scrollY, [0, 700], [0, reduce ? 0 : 80]);

  const onMove = (e) => {
    if (reduce || !imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 14);
    rx.set(-py * 14);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="hero" data-testid="section-hero" className="relative pt-[68px]">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-1 items-stretch lg:grid-cols-12">
          {/* text ~60% */}
          <div className="flex flex-col justify-center py-10 lg:col-span-7 lg:py-24 lg:pr-16">
            <Reveal>
              <span className="eyebrow" style={{ color: ACCENTS.blue }}>
                Nowa generacja nakrętek
              </span>
            </Reveal>
            <h1 className="mt-6 font-heading text-4xl font-black leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-[76px]">
              <ScrambleText text="OWOLOVO" duration={800} />
              <br />
              <span style={{ color: ACCENTS.blue }}>
                <ScrambleText text="Connect Caps." duration={1100} />
              </span>
            </h1>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-graphite md:text-lg">
                Poznaj OWOCAPS - kolekcjonerskie zakrętki-klocki OWOLOVO, które zmieniają każdy mus w
                zaproszenie do budowania, zbierania i tworzenia własnego świata.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 font-heading text-3xl font-black uppercase tracking-tight md:text-4xl">
                {[
                  { w: "Zbieraj.", c: ACCENTS.red },
                  { w: "Łącz.", c: ACCENTS.blue },
                  { w: "Buduj.", c: ACCENTS.green },
                ].map((item, i) => (
                  <motion.span
                    key={item.w}
                    style={{ color: item.c }}
                    animate={reduce ? {} : { y: [0, -8, 0] }}
                    transition={reduce ? {} : { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
                  >
                    {item.w}
                  </motion.span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <CtaButton
                  variant="black"
                  burst
                  burstColor={ACCENTS.pink}
                  data-testid="hero-cta-potencjal"
                  onClick={() => scrollTo("#values")}
                >
                  Zobacz potencjał <ArrowDown size={16} />
                </CtaButton>
                <CtaButton
                  variant="outline"
                  data-testid="hero-cta-kampania"
                  onClick={() => scrollTo("#metrics")}
                >
                  Zobacz kampanię <ArrowRight size={16} />
                </CtaButton>
              </div>
            </Reveal>
          </div>

          {/* WOW hero: big OWOLOVO MUS surrounded by vibrant floating raspberries */}
          <div className="relative lg:col-span-5 lg:border-l lg:border-hair">
            <motion.div
              ref={imgRef}
              onMouseMove={onMove}
              onMouseLeave={reset}
              style={{ y: parallax }}
              className="relative flex h-[440px] w-full items-center justify-center border-t border-hair lg:h-full lg:border-t-0"
            >
              {[
                { src: "/caps/rasp1.png", top: "1%", left: "4%", size: 96, op: 1, dur: 5, rot: 8 },
                { src: "/caps/rasp3.png", top: "4%", left: "70%", size: 128, op: 1, dur: 6.2, rot: -10 },
                { src: "/caps/rasp2.png", top: "70%", left: "0%", size: 104, op: 1, dur: 5.5, rot: 12 },
                { src: "/caps/rasp1.png", top: "76%", left: "72%", size: 116, op: 1, dur: 6.6, rot: -8 },
                { src: "/caps/rasp2.png", top: "40%", left: "-6%", size: 68, op: 0.5, dur: 7, rot: 6 },
                { src: "/caps/rasp3.png", top: "30%", left: "84%", size: 74, op: 0.45, dur: 6, rot: -6 },
                { src: "/caps/rasp1.png", top: "58%", left: "44%", size: 52, op: 0.4, dur: 5.2, rot: 10 },
                { src: "/caps/rasp2.png", top: "12%", left: "42%", size: 46, op: 0.5, dur: 6.4, rot: -12 },
              ].map((b, i) => (
                <motion.img
                  key={i}
                  src={b.src}
                  alt=""
                  aria-hidden
                  loading="eager"
                  className="pointer-events-none absolute z-20 select-none object-contain"
                  style={{ top: b.top, left: b.left, width: b.size, opacity: b.op }}
                  animate={reduce ? {} : { y: [0, -14, 0], rotate: [b.rot, -b.rot, b.rot] }}
                  transition={
                    reduce
                      ? {}
                      : {
                          y: { duration: b.dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 },
                          rotate: { duration: b.dur + 1.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 },
                        }
                  }
                />
              ))}
              <motion.img
                src={IMAGES.heroCap}
                alt="OWOLOVO MUS - jabłko, brzoskwinia, malina"
                loading="eager"
                style={{ rotateX: srx, rotateY: sry, transformPerspective: 1000 }}
                animate={reduce ? {} : { y: [0, -16, 0], rotate: [-2, 2, -2] }}
                transition={
                  reduce
                    ? {}
                    : {
                        y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                        rotate: { duration: 7, repeat: Infinity, ease: "easeInOut" },
                      }
                }
                className="relative z-10 h-[98%] w-auto max-h-[640px] object-contain drop-shadow-[0_25px_40px_rgba(233,110,160,0.25)]"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
